import React, { useEffect, useState } from "react";
import { adminApi, formatErr } from "./api";
import { useEscapeKey } from "./hooks";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, X, Warehouse as WarehouseIcon, MapPin } from "lucide-react";

const empty = { name: "", city: "", address: "", capacity: 0, active: true };

export default function AdminWarehouses() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  useEscapeKey(open, () => setOpen(false));
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    const { data } = await adminApi.get("/warehouses");
    setItems(data.warehouses);
  };
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing(null); setForm(empty); setOpen(true); };
  const openEdit = (w) => {
    setEditing(w.id);
    setForm({ name: w.name, city: w.city, address: w.address || "", capacity: w.capacity || 0, active: w.active !== false });
    setOpen(true);
  };

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const payload = { ...form, capacity: Number(form.capacity) || null, active: !!form.active };
      if (editing) await adminApi.put(`/warehouses/${editing}`, payload);
      else await adminApi.post("/warehouses", payload);
      toast.success(editing ? "Warehouse updated" : "Warehouse created");
      setOpen(false);
      await load();
    } catch (err) {
      toast.error(formatErr(err.response?.data?.detail));
    } finally { setBusy(false); }
  };

  const remove = async (id, name) => {
    if (!confirm(`Delete warehouse "${name}"?`)) return;
    try {
      await adminApi.delete(`/warehouses/${id}`);
      toast.success("Deleted");
      await load();
    } catch (err) { toast.error(formatErr(err.response?.data?.detail)); }
  };

  return (
    <div className="space-y-6" data-testid="admin-warehouses-page">
      <div className="flex items-center justify-between">
        <div>
          <p className="label-eyebrow text-gold">Locations</p>
          <h1 className="font-serif text-4xl text-bone mt-1">Warehouses</h1>
        </div>
        <button onClick={openNew} className="btn-primary !py-3" data-testid="new-warehouse-btn">
          <Plus className="w-3.5 h-3.5" /> New warehouse
        </button>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
        {items.map((w) => (
          <div key={w.id} className="bg-ink-900 border border-white/5 p-6 group" data-testid={`warehouse-card-${w.id}`}>
            <div className="flex items-start justify-between">
              <WarehouseIcon className="w-5 h-5 text-gold stroke-[1.5]" />
              <span className={`label-eyebrow ${w.active ? "text-gold" : "text-rose-300"}`}>{w.active ? "Active" : "Inactive"}</span>
            </div>
            <h3 className="font-serif text-xl text-bone mt-4">{w.name}</h3>
            <p className="flex items-center gap-1.5 text-mist text-xs mt-1.5">
              <MapPin className="w-3 h-3" /> {w.city}
            </p>
            {w.address && <p className="text-mist text-xs font-light mt-2">{w.address}</p>}
            {w.capacity ? (
              <p className="text-bone text-sm mt-3 font-light">Capacity · <span className="text-gold">{w.capacity.toLocaleString("en-IN")}</span> units</p>
            ) : null}
            <div className="flex gap-3 mt-5 pt-4 border-t border-white/5">
              <button onClick={() => openEdit(w)} className="flex items-center gap-1.5 text-mist hover:text-gold text-xs label-eyebrow" data-testid={`edit-warehouse-${w.id}`}>
                <Pencil className="w-3 h-3" /> Edit
              </button>
              <button onClick={() => remove(w.id, w.name)} className="flex items-center gap-1.5 text-mist hover:text-rose-300 text-xs label-eyebrow ml-auto" data-testid={`delete-warehouse-${w.id}`}>
                <Trash2 className="w-3 h-3" /> Delete
              </button>
            </div>
          </div>
        ))}
        {items.length === 0 && <p className="text-mist italic font-light">No warehouses yet.</p>}
      </div>

      {open && (
        <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-6">
          <form onClick={(e) => e.stopPropagation()} onSubmit={save} className="bg-ink-900 border border-white/10 w-full max-w-lg p-8 space-y-4" data-testid="warehouse-form-dialog">
            <div className="flex justify-between items-center">
              <h3 className="font-serif text-2xl text-bone">{editing ? "Edit warehouse" : "New warehouse"}</h3>
              <button type="button" onClick={() => setOpen(false)} className="text-mist hover:text-gold"><X className="w-5 h-5" /></button>
            </div>
            <input required value={form.name} onChange={set("name")} placeholder="Warehouse name" className="w-full bg-ink-950 border border-white/10 px-3 py-2.5 text-sm font-light text-bone outline-none focus:border-gold" data-testid="warehouse-name-input" />
            <input required value={form.city} onChange={set("city")} placeholder="City" className="w-full bg-ink-950 border border-white/10 px-3 py-2.5 text-sm font-light text-bone outline-none focus:border-gold" data-testid="warehouse-city-input" />
            <input value={form.address} onChange={set("address")} placeholder="Address" className="w-full bg-ink-950 border border-white/10 px-3 py-2.5 text-sm font-light text-bone outline-none focus:border-gold" />
            <input type="number" value={form.capacity} onChange={set("capacity")} placeholder="Capacity (units)" className="w-full bg-ink-950 border border-white/10 px-3 py-2.5 text-sm font-light text-bone outline-none focus:border-gold" />
            <label className="flex items-center gap-2 text-bone text-sm font-light">
              <input type="checkbox" checked={!!form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} className="accent-gold" />
              Active
            </label>
            <div className="flex justify-end gap-3 pt-3">
              <button type="button" onClick={() => setOpen(false)} className="btn-ghost !py-3">Cancel</button>
              <button type="submit" disabled={busy} className="btn-primary !py-3" data-testid="save-warehouse-btn">{busy ? "Saving…" : "Save"}</button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
